<script lang="ts">
import { ComplaintActivityLog, ComplaintAdditionalDoc, ComplaintDepartmentInfo, ComplaintInfo } from '@/interface/Complaint.interface';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import Breadcrumb from '@/views/complaint/breadcrumb.vue';
import RequestHeader from '@/components/pages/complaint/detail/header.vue';
import RequestInformation from '@/components/pages/complaint/detail/information.vue';
import RequestAdditionalDoc from '../shared/reqDoc.vue';
import DpmAnswer from '../shared/dpmAnswer.vue';
import RequestAttachment from '../shared/attachment.vue';
import AirlineAnswer from '../shared/airlineAnswer.vue';
import SendToAirline from './sendToAirline.vue';
import Return from './return.vue';
import RequestHistory from '../asc/history.vue';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ADD_DOCS_STATUS, DPM_BTN_KEY, DPM_STATUS, DPM_STATUS_OPTIONS, LANG_EN, REQUEST_STATUS } from '@/app/const';
import { dpmRequestBreadcrumb } from '@/components/utils';
import { complaintService, fileService, masterService, userService } from '@/services/serviceProvider';
import { ComplaintDepartmentInfoSave } from '@/interface/SaveComplaint.interface';
import { groupBy } from '@/app/common/types/groupBy.type';
import ComplaintChat from '../shared/chat.vue';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { UploadResponse } from '@/interface/FileUpload.interface';
import { ResponsiblePerson } from '@/interface/ResponsiblePerson.interface';
import { SelectItemType } from '@/app/common/types/selectItem.type';
import { UserEndReason } from '@/interface/UserEndReason.interface';
import RequestAppeal from '@/components/pages/complaint/detail/appeal.vue';
import AllAnswer from './allAnswer.vue';

export default {
    components: {
        Breadcrumb,
        RequestHeader,
        RequestInformation,
        RequestAdditionalDoc,
        DpmAnswer,
        RequestAttachment,
        AirlineAnswer,
        SendToAirline,
        Return,
        ComplaintChat,
        RequestHistory,
        RequestAppeal,
        AllAnswer
    },
    setup() {
        const router = useRouter();
        const currentRoute = router.currentRoute.value;
        // components
        const allAns = ref();
        // pdf
        const pdfContent = ref();

        return { router, currentRoute, pdfContent, allAns }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
        'displayAlertDialog',
        'exportPdf',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            breadcrumb: [...dpmRequestBreadcrumb],
            loading: false,
            showSummaryView: false,
            modal: {
                displayReqDoc: false as boolean,
                displaySendToAirline: false as boolean,
                displayReturnReason: false as boolean,
                displayChat: false as boolean,
                displayHistory: false as boolean,
                displaySummaryOptions: false as boolean
            },
            panel: [] as string[],
            dpmStatus: DPM_STATUS,
            dpmStatuses: DPM_STATUS_OPTIONS as SelectItemType[],
            selectedDepartmentCode: undefined as string | undefined,
            userEndReasons: [] as UserEndReason[],
            lang: this.$i18n.locale,
        }
    },
    watch: {
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        isNew() {
            return this.currentRoute.params.requestNo === undefined;
        },
        allAdditionalDocs() {
            const ascDocs = this.model.ascDepartment?.additionalDocs || [];
            const mainDpmDocs = this.model.relatedMainDepartment?.additionalDocs || [];
            let subDpmDocs = [] as ComplaintAdditionalDoc[];
            this.model.relatedSubDepartments?.forEach(o => {
                const docs = o.additionalDocs || [];
                if (docs && docs.length > 0) {
                    subDpmDocs = subDpmDocs.concat(docs);
                }
            })
            return ascDocs.concat(mainDpmDocs).concat(subDpmDocs);
        },
        waitApproveDocs() {
            return this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.NEW)
        },
        uploadedDocs() {
            return this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.UPLOADED)
        },
        currentDpm() {
            return userService.currentDepartment();
        },
        isMainDpm(): boolean {
            return this.model.relatedMainDepartment?.departmentCode === userService.currentDepartment();
        },
        isSubDpm(): boolean {
            const dpmCodes = this.model.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDpm = userService.currentDepartment();
            if (dpmCodes && currentDpm) return dpmCodes.includes(currentDpm);
            return false;
        },
        isOwner(): boolean {
            // check owner of document
            let isOwner = false;
            if (this.responsibleEmpId === userService.currentUserInfo()?.employeeId)
                isOwner = true;

            return isOwner
        },
        viewMode(): boolean {
            // check status
            let editable = false;
            if (this.isMainDpm)
                editable = (this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && (this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.IN_PROGRESS
                        || this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.UPLOADED
                        || this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.ACCEPTED))
                    || (this.model.status === REQUEST_STATUS.REQUEST_DOC && !this.isDpmRequestDoc)
            else if (this.isSubDpm) {
                const sub = this.model.relatedSubDepartments?.find(o => o.departmentCode === this.currentDpm);
                editable = (this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && (sub?.departmentStatus === DPM_STATUS.IN_PROGRESS
                        || sub?.departmentStatus === DPM_STATUS.UPLOADED
                        || sub?.departmentStatus === DPM_STATUS.ACCEPTED))
                    || (this.model.status === REQUEST_STATUS.REQUEST_DOC && !this.isDpmRequestDoc);
            }

            return !editable;
        },
        waitAirline() {
            // check status
            let waitAirline = false;
            if (this.isMainDpm)
                waitAirline = this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.WAIT_AIRLINE
            else if (this.isSubDpm) {
                const sub = this.model.relatedSubDepartments?.find(o => o.departmentCode === this.currentDpm);
                waitAirline = this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && sub?.departmentStatus === DPM_STATUS.WAIT_AIRLINE;
            }

            return waitAirline;
        },
        responsibleEmpId(): string | undefined {
            let responsibleEmpId = undefined as string | undefined;
            if (this.isMainDpm)
                responsibleEmpId = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            else if (this.isSubDpm)
                responsibleEmpId = this.model.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm)?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            return responsibleEmpId;
        },
        activeDepartment: {
            get() {
                if (this.isMainDpm) {
                    return this.model.relatedMainDepartment;
                } else if (this.isSubDpm) {
                    return this.model.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm);
                }
                return undefined;
            },
            set(newValue: ComplaintDepartmentInfo) {
                if (this.isMainDpm) {
                    this.model.relatedMainDepartment = newValue;
                } else if (this.isSubDpm) {
                    let sub = this.model.relatedSubDepartments?.find(s => s.departmentCode === this.currentDpm);
                    if (sub) sub = newValue;
                }
            }
        },
        allWatingAdditionalDocs() {
            return this.allAdditionalDocs?.filter(o => o.status === ADD_DOCS_STATUS.APPROVED);
        },
        isDpmRequestDoc() {
            let result = false as boolean;
            if (this.model.status === REQUEST_STATUS.REQUEST_DOC) {
                const grouped = groupBy(this.allWatingAdditionalDocs, ['departmentCode'])
                Object.entries(grouped).forEach(([departmentCode, docs]) => {
                    if (!result) {
                        let activeDpmCode = this.activeDepartment?.departmentCode;
                        if (activeDpmCode && departmentCode === activeDpmCode) {
                            result = true;
                        }
                    }
                });
            }
            return result;
        },
        relatedWithAirline() {
            return this.model.subTopicInfo.flightUnits && this.model.subTopicInfo.flightUnits.length > 0;
        },
        allSubsEnd() {
            const subs = this.model.relatedSubDepartments;
            return subs?.length === 0 || (subs?.length === subs?.filter(o => o.departmentStatus === DPM_STATUS.END
                || o.departmentStatus === DPM_STATUS.RETURN)?.length);
        },
        filteredBreadcrumb() {
            switch (this.model.status) {
                case REQUEST_STATUS.IN_PROGRESS:
                    return this.getBreadcrumbInprog;
                case REQUEST_STATUS.REQUEST_DOC:
                    if (this.isDpmRequestDoc) {
                        return this.breadcrumb.filter(o => o.key === DPM_BTN_KEY.EXPORT
                            || o.key === DPM_BTN_KEY.HISTORY)
                    } else {
                        return this.getBreadcrumbInprog.filter(o => o.key !== DPM_BTN_KEY.NEED_DOC);
                    }
                default:
                    return this.breadcrumb.filter(o => o.key === DPM_BTN_KEY.EXPORT
                        || o.key === DPM_BTN_KEY.HISTORY
                    )
            }
        },
        getBreadcrumbInprog() {
            if (this.responsibleEmpId && this.responsibleEmpId === userService.currentUserInfo()?.employeeId) {
                switch (this.activeDepartment?.departmentStatus) {
                    case DPM_STATUS.IN_PROGRESS:
                        let bInprog = [...this.breadcrumb];
                        if (!this.relatedWithAirline) {
                            bInprog = bInprog.filter(o => o.key !== DPM_BTN_KEY.SEND_AIRLINE)
                        }
                        if (this.isMainDpm && !this.allSubsEnd) {
                            bInprog = bInprog.filter(o => o.key !== DPM_BTN_KEY.SUMMARY)
                        }
                        return bInprog;
                    case DPM_STATUS.ACCEPTED:
                        let bAccepted = this.breadcrumb.filter(o => o.key === DPM_BTN_KEY.EXPORT
                            || o.key === DPM_BTN_KEY.HISTORY || o.key === DPM_BTN_KEY.SUMMARY
                            || o.key === DPM_BTN_KEY.SEND_AIRLINE || o.key === DPM_BTN_KEY.SAVE)
                        if (this.isMainDpm && !this.allSubsEnd) {
                            bAccepted = bAccepted.filter(o => o.key !== DPM_BTN_KEY.SUMMARY)
                        }
                        return bAccepted;
                    default:
                        return this.breadcrumb.filter(o => o.key === DPM_BTN_KEY.EXPORT
                            || o.key === DPM_BTN_KEY.HISTORY)
                }
            }
            return this.breadcrumb.filter(o => o.key === DPM_BTN_KEY.EXPORT
                || o.key === DPM_BTN_KEY.HISTORY
            )
        },
        isMainResponsible() {
            let mainResponsiblePerson = undefined as ResponsiblePerson | undefined;
            if (this.isMainDpm) {
                mainResponsiblePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.isMainResponsible)
            } else if (this.isSubDpm) {
                mainResponsiblePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.isMainResponsible)
            }
            return userService.currentUserInfo()?.employeeId === mainResponsiblePerson?.employeeId;
        },
        isSubmittedByAirline() {
            return this.activeDepartment?.departmentStatus === DPM_STATUS.ACCEPTED
        },
        enableAscEndReason(): boolean {
            return this.model.status === REQUEST_STATUS.END
                && this.model.ascEndReason !== undefined
                && this.model.ascEndReason !== null;
        },
        enableUserEndReason(): boolean {
            return this.model.status === REQUEST_STATUS.END
                && (this.model.ascEndReason === undefined
                    || this.model.ascEndReason === null);
        },
    },
    async mounted() {
        this.panel = this.isMainDpm || this.isSubDpm ? this.currentDpm ? [this.currentDpm] : [] : [];
        await this.getUserEndReasons();
    },
    methods: {
        async getUserEndReasons() {
            try {
                const res = await masterService.getEndReasons();
                if (res && res.status === 200) {
                    this.userEndReasons = res.data as UserEndReason[];
                }
            } catch (e) {
            }
        },
        allLogs(departmentCode: string | undefined) {
            const activitiesLogs = this.model.activitiesLogs || [];
            activitiesLogs.forEach(o => o.logType = 'activity');
            let chatLogs = this.model.chatLogs || [];
            if (departmentCode) {
                // หา external department ที่เกี่ยวข้องกับฝ่ายที่กำลังเปิดดู
                let externalDpm = undefined as string | undefined;
                if (this.model.relatedMainDepartment?.departmentCode === departmentCode) {
                    externalDpm = this.model.relatedMainDepartment.external?.externalDepartment;
                } else if (this.model.relatedSubDepartments?.find(o => o.departmentCode === departmentCode)) {
                    externalDpm = this.model.relatedSubDepartments?.find(o => o.departmentCode === departmentCode)?.external?.externalDepartment;
                }
                // หากได้ external department แล้วก็เอาไปกรอง chat ออกมาเฉพาะสายการบินที่เกี่ยวข้องกับฝ่ายที่กำลังเปิดดู
                if (externalDpm)
                    chatLogs = chatLogs.filter(o => o.departmentCode === departmentCode || o.createBy === externalDpm);
            }
            chatLogs.forEach(o => o.logType = 'chat');
            return activitiesLogs.concat(chatLogs);
        },
        sortedAllLogs(departmentCode: string | undefined) {
            return this.allLogs(departmentCode).sort((a, b) => (a.createDate && b.createDate ? a.createDate > b.createDate ? -1 : 1 : 1));
        },
        unreadChatCount(departmentCode: string | undefined) {
            const chatLogs = this.sortedAllLogs(departmentCode).filter(o => o.logType === 'chat');
            if (chatLogs && chatLogs.length > 0) {
                if (userService.isAsc() || userService.isDpm()) {
                    let externalDepartment = this.activeDepartment?.external?.externalDepartment;
                    return chatLogs.filter(o => !o.departmentCode && !o.read && o.createBy === externalDepartment)?.length;
                } else if (userService.isAirline()) {
                    let externalDepartment = userService.currentUserInfo()?.username;
                    let dpmCode = undefined as string | undefined;
                    if (this.activeDepartment?.external?.externalDepartment === externalDepartment)
                        dpmCode = this.activeDepartment?.departmentCode
                    return chatLogs.filter(o => o.departmentCode && !o.read && o.departmentCode === dpmCode)?.length;
                }
            }
            return 0;
        },
        async requestAdditionalDocs() {
            try {
                if (this.model.requestNo) {
                    const docs = this.isMainDpm
                        ? this.model.relatedMainDepartment?.additionalDocs
                        : this.model.relatedSubDepartments?.find(o => o.departmentCode === this.currentDpm)?.additionalDocs;
                    const res = await complaintService.requestAdditionalDocs(this.model.requestNo, docs)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            header: this.$t('t-save-success'),
                            messageTitle: this.$t('t-action-need-more-doc-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-need-more-doc-fail'),
                } as BaseAlert)
            }
        },
        goToListPage() {
            this.router.push({
                path: `/complaint-list`
            })
        },
        displayConfirmDialog(model: BaseConfirm) {
            this.$emit('displayConfirmDialog', model);
        },
        displayAlertDialog(model: BaseAlert) {
            this.$emit('displayAlertDialog', model);
        },
        async showSummaryOptions() {
            let isFormValid = false;
            if (this.isMainDpm) {
                if (this.allAns && this.allAns.getDpmAns()) {
                    const valid = await this.allAns.getDpmAns().validateForm();
                    if (valid) {
                        isFormValid = true;
                    } else {
                        this.displayAlertDialog({
                            success: false,
                            messageTitle: this.$t('t-complaint-input-require'),
                        } as BaseAlert)
                    }
                }
            } else if (this.isSubDpm) {
                if (this.allAns && this.allAns.getDpmAns()[0]) {
                    const valid = await this.allAns.getDpmAns()[0].validateForm();
                    if (valid) {
                        isFormValid = true;
                    } else {
                        this.displayAlertDialog({
                            success: false,
                            messageTitle: this.$t('t-complaint-input-require'),
                        } as BaseAlert)
                    }
                }
            }
            // if form valid -> check main responsible to open summary options dialog
            if (isFormValid) {
                if (this.isMainResponsible)
                    this.confirmSummary();
                else
                    this.modal.displaySummaryOptions = true;
            }
        },
        async uploadFileBeforeSave() {
            try {
                let allUploads = [] as AttachmentUploaderType[];
                if (this.activeDepartment?.fileUploads && this.activeDepartment?.fileUploads.length > 0) {
                    if (!this.activeDepartment?.attachments) this.activeDepartment.attachments = [];
                    allUploads = allUploads.concat([...this.activeDepartment?.fileUploads]);
                }

                if (allUploads && allUploads.length > 0) {
                    // prepare payloads
                    let payloads = [] as File[];
                    allUploads.forEach(attach => {
                        if (attach.blob) {
                            const blob = fileService.dataURLToBlob(attach.blob);
                            let file = new File([blob], attach.name, { type: attach.fileType })
                            payloads.push(file);
                        }
                    });
                    // call upload API
                    const res = await fileService.upload(payloads);
                    if (res && res.status === 201) {
                        const data = res.data as UploadResponse;
                        data.files.forEach(file => {
                            let attachment = {
                                itemNo: -1,
                                fileName: file.fileName,
                                runningNo: -1,
                                size: allUploads?.find(upload => upload.name === file.fileName)?.size,
                                url: file.objectName,
                            } as AttachmentUrl;
                            // add to attachment
                            if (this.activeDepartment?.fileUploads?.find(o => o.name === attachment.fileName) !== undefined) {
                                this.activeDepartment?.attachments?.push(attachment);
                            }
                        });
                        // clear attach uploads
                        if (this.activeDepartment) {
                            this.activeDepartment.fileUploads = [];
                        }
                    } else {
                        throw new Error();
                    }
                }
            } catch (e: any) {
                throw new Error("Upload Error");
            }
        },
        async confirmSummary() {
            this.displayConfirmDialog({
                messageTitle: this.$t('t-action-summary-confirm'),
                callBack: this.doSummaryToAsc
            } as BaseAlert)
        },
        async doSummaryToAsc() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                        relatedLaw: this.activeDepartment?.relatedLaw,
                        managementDetail: this.activeDepartment?.managementDetail,
                        operationResult: this.activeDepartment?.operationResult,
                        otherRemark: this.activeDepartment?.otherRemark,
                        staffOpinionCode: this.activeDepartment?.staffOpinionCode,
                        attachments: this.activeDepartment?.attachments,
                    } as ComplaintDepartmentInfoSave;
                    const res = await complaintService.summaryRequest(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-summary-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-summary-fail'),
                } as BaseAlert)
            }
        },
        async confirmSummaryToMainResponsible() {
            this.displayConfirmDialog({
                messageTitle: this.$t('t-action-summary-to-main-responsible-confirm'),
                callBack: this.doSummaryToMainResponsible
            } as BaseAlert)
        },
        async doSummaryToMainResponsible() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                        relatedLaw: this.activeDepartment?.relatedLaw,
                        managementDetail: this.activeDepartment?.managementDetail,
                        operationResult: this.activeDepartment?.operationResult,
                        otherRemark: this.activeDepartment?.otherRemark,
                        staffOpinionCode: this.activeDepartment?.staffOpinionCode,
                        attachments: this.activeDepartment?.attachments,
                    } as ComplaintDepartmentInfoSave;
                    const res = await complaintService.summaryToMainResponsible(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-summary-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-summary-fail'),
                } as BaseAlert)
            }
        },
        async doSendBackToASC(dpmInfo: ComplaintDepartmentInfo) {
            try {
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: dpmInfo.departmentCode,
                        departmentType: dpmInfo.departmentType,
                        otherRemark: dpmInfo.otherRemark,
                    } as ComplaintDepartmentInfoSave;
                    const res = await complaintService.sendRequestToAsc(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-send-to-asc-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-send-to-asc-fail'),
                } as BaseAlert)
            }
        },
        async doSendToAirline() {
            try {
                if (this.model.requestNo) {
                    let payloads: any = {
                        externalDepartment: this.activeDepartment?.external?.externalDepartment,
                        externalDepartmentName: this.activeDepartment?.external?.externalDepartmentName,
                        externalDepartmentEmail: this.activeDepartment?.external?.externalDepartmentEmail,
                        departmentCode: this.activeDepartment?.departmentCode,
                        departmentType: this.activeDepartment?.departmentType,
                    }
                    const res = await complaintService.sendRequestToAirline(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-action-send-to-airline-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-send-to-airline-fail'),
                } as BaseAlert)
            }
        },
        confirmSave() {
            this.displayConfirmDialog({
                messageTitle: this.$t('t-confirm-save'),
                callBack: this.doDpmSave
            } as BaseAlert)
        },
        async doDpmSave() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let dpm = this.isMainDpm ? this.model.relatedMainDepartment
                        : this.model.relatedSubDepartments?.find(o => o.departmentCode === userService.currentDepartment());
                    if (dpm) {
                        let payloads = {
                            departmentCode: dpm.departmentCode,
                            departmentType: dpm.departmentType,
                            relatedLaw: dpm.relatedLaw,
                            managementDetail: dpm.managementDetail,
                            operationResult: dpm.operationResult,
                            otherRemark: dpm.otherRemark,
                            staffOpinionCode: dpm.staffOpinionCode,
                            attachments: dpm.attachments,
                        }
                        const res = await complaintService.ascSave(this.model.requestNo, payloads)
                        if (res && res.status === 200) {
                            this.displayAlertDialog({
                                success: true,
                                messageTitle: this.$t('t-save-success'),
                                callBack: this.goToListPage
                            } as BaseAlert)
                        }
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-upload-file-fail'),
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-save-fail'),
                    } as BaseAlert)
                }
            }
        },
        getPdfContent() {
            return this.pdfContent;
        },
        async sendChat(log: ComplaintActivityLog) {
            try {
                if (this.model.requestNo) {
                    await complaintService.saveChat(this.model.requestNo, { detail: log.detail })
                    if (!this.model.chatLogs) this.model.chatLogs = [];
                    this.model.chatLogs.push(log);
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-complaint-chat-send-message-fail'),
                } as BaseAlert)
            }
        },
        collapse(value: string) {
            if (this.panel)
                this.panel = this.panel.filter(o => o != value);
        },
        expand(value: string) {
            if (this.panel)
                this.panel.push(value);
        },
        dpmColor(status: string | undefined) {
            return this.dpmStatuses.find(o => o.value === status)?.color;
        },
        dpmStatusLabel(status: string | undefined) {
            const label = this.dpmStatuses.find(o => o.value === status)?.label;
            return label ? this.$t(`t-${label}`) : '';
        },
        openDpmChat(departmentCode: string | undefined) {
            this.selectedDepartmentCode = departmentCode;
            this.modal.displayChat = true;
            this.readChat();
        },
        userEndReasonText() {
            const endReason = this.userEndReasons.find(o => o.id === this.model.userEndReasonId)
            return this.lang === LANG_EN ? endReason?.reasonEn : endReason?.reasonTh;
        },
        async readChat() {
            try {
                if (this.model.requestNo) {
                    await complaintService.readChat(this.model.requestNo)
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-complaint-chat-read-message-fail'),
                } as BaseAlert)
            }
        }
    },
};
</script>

<template>
    <div class="bg-page-home main-page"></div>
    <div class="bg-page-home-content complaint-detail app-form-input">
        <Breadcrumb :title="$t('t-complaint')" v-model="filteredBreadcrumb" :is-compact="true"
            @do-need-additional-docs="modal.displayReqDoc = true" @view-history="modal.displayHistory = true"
            @do-send-to-airline="modal.displaySendToAirline = true" @do-summary-of-operation="showSummaryOptions"
            @do-send-back-to-asc="modal.displayReturnReason = true" @do-export-pdf="$emit('exportPdf')"
            @do-dpm-save="confirmSave">
        </Breadcrumb>
        <div ref="pdfContent" class="pdf-content">
            <RequestHeader v-if="!isNew && model" v-model="model" :view-mode="viewMode && isOwner"
                @display-alert-dialog="displayAlertDialog"></RequestHeader>
            <RequestAppeal v-if="!isNew && model" v-model="model"></RequestAppeal>
            <!-- ASC end-request reason -->
            <v-card v-if="enableAscEndReason" class="app-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t('t-complaint-answer-back-info') }}</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <v-textarea v-model.trim="model.ascEndReason" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ model.ascEndReason ?? '-' }}
                    </div>
                </v-card-text>
            </v-card>
            <!-- User end-request reason -->
            <v-card v-if="enableUserEndReason" class="app-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t('t-complaint-user-end-reason') }}</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <v-textarea v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                        v-model.trim="model.userEndReason" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <v-textarea v-else :value="userEndReasonText()" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <div v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                        class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ model.userEndReason ?? '-' }}
                    </div>
                    <div v-else class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ userEndReasonText() ?? '-' }}
                    </div>
                </v-card-text>
            </v-card>

            <!-- answer & status from each department -->
            <AllAnswer ref="allAns" v-model="model" :active-department="activeDepartment"
                :unread-chat-count="unreadChatCount(activeDepartment?.departmentCode)" @open-dpm-chat="openDpmChat"
                @display-alert-dialog="displayAlertDialog">
            </AllAnswer>

            <!-- answer from airline -->
            <AirlineAnswer v-if="activeDepartment?.external && activeDepartment.external.externalDepartmentAnswer
                && (activeDepartment?.departmentStatus === dpmStatus.ACCEPTED
                    || activeDepartment?.departmentStatus === dpmStatus.END)" v-model="activeDepartment.external"
                :complaint="model">
            </AirlineAnswer>

            <!-- all requested additional document(s) -->
            <RequestAttachment v-model="allAdditionalDocs" :user-mode="false" :show-only-self-dpm="true">
            </RequestAttachment>
            <!-- request information -->
            <RequestInformation v-model="model"></RequestInformation>
        </div>

        <!-- bellow components is all MODAL -->
        <!-- Dpm view histories-->
        <RequestHistory v-model="modal.displayHistory" :items="model.activitiesLogs"></RequestHistory>
        <!-- Dpm request additional doc(s) -->
        <RequestAdditionalDoc v-model="modal.displayReqDoc"
            :complaintDepartment="isMainDpm ? model.relatedMainDepartment : model.relatedSubDepartments?.find(o => o.departmentCode === currentDpm)"
            @on-confirm="displayConfirmDialog" @do-request-additional-docs="requestAdditionalDocs"
            @display-alert-dialog="displayAlertDialog">
        </RequestAdditionalDoc>
        <!-- Dpm send to airline -->
        <SendToAirline v-model="modal.displaySendToAirline" :complaint="model" :disabled="isSubmittedByAirline"
            @on-confirm="doSendToAirline">
        </SendToAirline>
        <Return v-model="modal.displayReturnReason" :complaint="activeDepartment" @on-confirm="doSendBackToASC">
        </Return>
        <ComplaintChat v-model="modal.displayChat" :complaint="model" :is-owner="isOwner" :waitAirline="waitAirline"
            :view-mode="viewMode || (currentDpm !== selectedDepartmentCode)"
            :filter-department-code="selectedDepartmentCode" @on-confirm="sendChat">
        </ComplaintChat>
        <v-dialog v-model="modal.displaySummaryOptions" width="30%" :close-on-back="false" persistent>
            <v-card class="dialog-card">
                <v-card-text class="py-6 my-6">
                    <div class="w-100 d-flex flex-column">
                        <v-btn @click="confirmSummary" class="mb-6" variant="flat" color="primary" size="large">
                            {{ $t('t-action-summary-asc') }}
                        </v-btn>
                        <v-btn @click="confirmSummaryToMainResponsible" variant="flat" color="primary" size="large">
                            {{ $t('t-action-summary-main-resp') }}
                        </v-btn>
                    </div>
                </v-card-text>
                <v-card-actions class="my-6">
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="modal.displaySummaryOptions = false" variant="outlined" color="primary"
                                size="large" block>
                                {{ $t('t-cancel') }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>